import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { LicenseSummary, Page, Project } from "../lib/types";
import { Button, Card, ErrorNote, Field, Select } from "./ui";

/**
 * Issuing a licence, from the licences screen or from a project's own page.
 *
 * The input is a project, not a customer and a plan. A licence unlocks
 * something the customer owns, and the thing they own is the project — so its
 * customer, product and plan all come from there. Asking for the pair
 * separately created a second project every time somebody issued a licence by
 * hand, which is where the orphaned projects came from.
 *
 * Pass `project` to issue against a known one; omit it to choose from the
 * projects that do not have a licence yet.
 */
export function IssueLicense({
  project,
  onClose,
}: {
  project?: Project;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const fixed = project ?? null;

  const [projectId, setProjectId] = useState(fixed?.id ?? "");
  const [hostnames, setHostnames] = useState((fixed?.hostnames ?? []).join("\n"));
  const [issuedKey, setIssuedKey] = useState<string | null>(null);

  // Only projects with nothing issued against them: a project carries at most
  // one licence, so the rest would only ever answer with a conflict.
  const projects = useQuery({
    queryKey: ["projects", "licensable"],
    queryFn: () => api<Page<Project>>("/api/v1/admin/projects?limit=100"),
    enabled: fixed === null,
  });
  const options = (projects.data?.items ?? []).filter((option) => option.license === null);

  const issue = useMutation({
    mutationFn: () =>
      api<{ key: string; license: LicenseSummary }>("/api/v1/admin/licenses", {
        method: "POST",
        body: {
          projectId,
          hostnames: hostnames
            .split(/[\s,]+/)
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
        },
      }),
    onSuccess: (result) => {
      setIssuedKey(result.key);
      void queryClient.invalidateQueries({ queryKey: ["licenses"] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      void queryClient.invalidateQueries({ queryKey: ["project"] });
    },
  });

  if (issuedKey !== null) {
    return (
      <Card title="Licence issued">
        <div className="space-y-4 p-4">
          <p className="text-sm text-muted">
            Copy this key now and send it to the customer. It is stored only as a hash, so this is
            the last time anyone can read it.
          </p>
          <code className="block break-all rounded border border-accent/40 bg-base px-3 py-3 tabular text-base text-ink">
            {issuedKey}
          </code>
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => void navigator.clipboard.writeText(issuedKey)}>
              Copy key
            </Button>
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Issue a licence">
      <form
        className="space-y-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          issue.mutate();
        }}
      >
        {fixed === null ? (
          <Field
            label="Project"
            hint="Only projects without a licence. Open a project first if the one you want is missing."
          >
            <Select
              value={projectId}
              required
              onChange={(event) => {
                setProjectId(event.target.value);
                const picked = options.find((option) => option.id === event.target.value);
                setHostnames((picked?.hostnames ?? []).join("\n"));
              }}
            >
              <option value="">
                {projects.isPending ? "Loading…" : "Select a project"}
              </option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.reference} · {option.name} — {option.customer.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <p className="text-sm text-muted">
            For <span className="tabular text-ink">{fixed.reference}</span> — {fixed.customer.name},{" "}
            {fixed.product.name} on {fixed.plan.name}.
          </p>
        )}

        <Field
          label="Allowed hostnames"
          hint="One per line or comma separated. A licence with no hostnames cannot activate anywhere."
        >
          <textarea
            value={hostnames}
            rows={3}
            placeholder="example.com&#10;www.example.com"
            className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 tabular text-ink placeholder:text-dim focus:border-accent"
            onChange={(event) => setHostnames(event.target.value)}
          />
        </Field>

        {issue.isError && <ErrorNote>{(issue.error as Error).message}</ErrorNote>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={issue.isPending || projectId === ""}>
            {issue.isPending ? "Issuing…" : "Issue licence"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
